import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {parseApiDate} from '../utils/dateTime';
import AuthImage from './AuthImage';
import PageSizeSelect from './PageSizeSelect';
import {usePageSize} from '../hooks/usePageSize';

/** Чат стилиста (admin-вид): владелец + статус удаления. */
interface AdminConversation {
    id: string;
    ownerId: string;
    ownerUsername?: string | null;
    title?: string | null;
    archived: boolean;
    deleted: boolean;
    deletedAt?: string | null;
    lastMessagePreview?: string | null;
    lastMessageAt?: string | null;
    createdAt?: string | null;
}

interface ToolCall { id?: string; name?: string; arguments?: string }
interface ToolExecution { tool?: string; arguments?: string; result?: string }
interface TraceStep {
    iteration?: number;
    forcedFinal?: boolean;
    response?: { model?: string; finishReason?: string; content?: string | null; toolCalls?: ToolCall[] };
    toolExecutions?: ToolExecution[];
}
interface TraceMessage {
    role?: string; content?: string | null; images?: number;
    toolCallId?: string; toolName?: string; toolCalls?: ToolCall[];
}
interface AgentTrace {
    model?: string; keySource?: string; systemPrompt?: string; toolNames?: string;
    finalText?: string; steps?: TraceStep[]; messages?: TraceMessage[];
}

/** Оценка пользователя на ответ (FR-C5): 👍/👎 + комментарий. */
interface AdminFeedback {
    rating?: string | null;
    comment?: string | null;
}

/** Сообщение чата (admin-вид) + полный трейс агента. */
interface AdminMessage {
    id: string;
    role: string;
    type: string;
    status: string;
    text?: string | null;
    payload?: unknown;
    attachments?: string[] | null;
    errorMessage?: string | null;
    trace?: AgentTrace | null;
    feedback?: AdminFeedback | null;
    createdAt?: string | null;
}

/** Сводка фидбека за период ({@code /api/admin/stylist/feedback/summary}). */
interface FeedbackSummary {
    days: number;
    up: number;
    down: number;
    negativeShare: number;
}

const fmt = (iso?: string | null): string => {
    if (!iso) return '—';
    const d = parseApiDate(iso);
    return d ? d.toLocaleString('ru-RU') : iso;
};

const short = (id: string): string => (id ? id.slice(0, 8) : '');

/** Красиво печатает JSON-строку аргументов/результата (или возвращает текст как есть). */
const pretty = (raw?: string): string => {
    if (raw == null) return '';
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
        return raw;
    }
};

/** Сводка оценок за 7/30 дней (FR-C5): 👍/👎 и доля негатива. */
const FeedbackSummaryBar: React.FC = () => {
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState<FeedbackSummary | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const axios = await createAxiosInstance();
                const {data} = await axios.get(`/admin/stylist/feedback/summary?days=${days}`);
                if (!cancelled) setSummary(data);
            } catch {
                if (!cancelled) setSummary(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [days]);

    return (
        <div className="card p-3 flex flex-wrap items-center gap-3" data-testid="feedback-summary">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Фидбек</span>
            <div className="flex items-center gap-1">
                {[7, 30].map(d => (
                    <button
                        key={d}
                        type="button"
                        onClick={() => setDays(d)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${days === d
                            ? 'bg-ink-900 text-white dark:bg-white dark:text-ink-900'
                            : 'text-gray-500 hover:bg-ink-50 dark:hover:bg-white/5'}`}
                    >
                        {d} дн.
                    </button>
                ))}
            </div>
            {loading && <span className="text-xs text-gray-400">Загрузка…</span>}
            {!loading && summary && (
                <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="feedback-summary-values">
                    <span className="badge badge-success">👍 {summary.up}</span>
                    <span className="badge badge-warning">👎 {summary.down}</span>
                    <span className="badge badge-neutral">
                        доля 👎: {Math.round((summary.negativeShare ?? 0) * 100)}%
                    </span>
                </div>
            )}
            {!loading && !summary && <span className="text-xs text-gray-400">Сводка недоступна</span>}
        </div>
    );
};

const StylistAdmin: React.FC = () => {
    const [conversations, setConversations] = useState<AdminConversation[]>([]);
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loadingConvs, setLoadingConvs] = useState(false);
    const [negativeOnly, setNegativeOnly] = useState(false);
    // Размер страницы списка диалогов выбирает администратор и он сохраняется (CTHS-7);
    // смена размера возвращает на первую страницу.
    const [size, setSize] = usePageSize('stylist-admin', {onChange: () => setPage(0)});

    const [selected, setSelected] = useState<AdminConversation | null>(null);
    const [messages, setMessages] = useState<AdminMessage[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadConversations = useCallback(async () => {
        setLoadingConvs(true);
        setError(null);
        try {
            const axios = await createAxiosInstance();
            const feedbackParam = negativeOnly ? '&feedback=negative' : '';
            const {data} = await axios.get(`/admin/stylist/conversations?page=${page}&size=${size}${feedbackParam}`);
            setConversations(Array.isArray(data?.content) ? data.content : []);
            setTotalElements(data?.totalElements ?? 0);
        } catch (e) {
            setError('Не удалось загрузить чаты');
        } finally {
            setLoadingConvs(false);
        }
    }, [page, size, negativeOnly]);

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    const selectConversation = async (conv: AdminConversation) => {
        setSelected(conv);
        setMessages([]);
        setLoadingMessages(true);
        try {
            const axios = await createAxiosInstance();
            const {data} = await axios.get(
                `/admin/stylist/conversations/${conv.id}/messages?page=0&size=100`);
            setMessages(Array.isArray(data?.content) ? data.content : []);
        } catch (e) {
            setError('Не удалось загрузить сообщения');
        } finally {
            setLoadingMessages(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalElements / size));

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Чаты AI-стилиста</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    Все чаты всех пользователей (включая удалённые) с полным трейсом агента.
                </p>
            </div>

            {error && <div className="badge badge-warning">{error}</div>}

            <FeedbackSummaryBar/>

            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer
                              min-h-[44px]">
                <input
                    type="checkbox"
                    checked={negativeOnly}
                    onChange={e => {
                        setPage(0);
                        setNegativeOnly(e.target.checked);
                    }}
                    data-testid="negative-only-filter"
                    className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
                />
                Только с 👎
            </label>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Список чатов */}
                <div className="card p-0 overflow-hidden self-start">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-gray-700/40 text-left text-gray-500 dark:text-gray-400">
                                <tr>
                                    <th className="px-3 py-2 font-medium">Чат</th>
                                    <th className="px-3 py-2 font-medium">Владелец</th>
                                    <th className="px-3 py-2 font-medium">Активность</th>
                                    <th className="px-3 py-2 font-medium">Статус</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {conversations.map((c) => (
                                    <tr
                                        key={c.id}
                                        onClick={() => selectConversation(c)}
                                        className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                                            selected?.id === c.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                        }`}
                                    >
                                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{c.title || 'Новый чат'}</td>
                                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300" title={c.ownerId}>{c.ownerUsername || short(c.ownerId)}</td>
                                        <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{fmt(c.lastMessageAt)}</td>
                                        <td className="px-3 py-2">
                                            {c.deleted && <span className="badge badge-warning mr-1">удалён</span>}
                                            {c.archived && <span className="badge badge-neutral mr-1">архив</span>}
                                            {!c.deleted && !c.archived && <span className="badge badge-success">активен</span>}
                                        </td>
                                    </tr>
                                ))}
                                {!loadingConvs && conversations.length === 0 && (
                                    <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">Чатов нет</td></tr>
                                )}
                                {loadingConvs && (
                                    <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-400">Загрузка…</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 dark:border-gray-700 text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Всего: {totalElements}</span>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                            <PageSizeSelect
                                table="stylist-admin"
                                label="Диалогов на странице"
                                value={size}
                                onChange={setSize}
                                totalElements={totalElements}
                            />
                            <button className="btn-secondary px-2 py-1" disabled={page <= 0}
                                    aria-label="Предыдущая страница"
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}>←</button>
                            <span className="text-gray-500 dark:text-gray-400">{page + 1} / {totalPages}</span>
                            <button className="btn-secondary px-2 py-1" disabled={page + 1 >= totalPages}
                                    aria-label="Следующая страница"
                                    onClick={() => setPage((p) => p + 1)}>→</button>
                        </div>
                    </div>
                </div>

                {/* Сообщения выбранного чата + трейс */}
                <div className="card max-h-[78vh] overflow-y-auto">
                    {!selected && <div className="text-center text-gray-400 py-10">Выберите чат слева</div>}
                    {selected && (
                        <div className="space-y-3">
                            <div className="text-xs text-gray-400 font-mono">
                                conv {selected.id} · owner {selected.ownerId}
                                {selected.deleted && ` · удалён ${fmt(selected.deletedAt)}`}
                            </div>
                            {loadingMessages && <div className="text-gray-400">Загрузка сообщений…</div>}
                            {messages.map((m) => <MessageRow key={m.id} message={m} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const MessageRow: React.FC<{message: AdminMessage}> = ({message}) => {
    const [showTrace, setShowTrace] = useState(false);
    const isUser = message.role === 'USER';
    const hasTrace = !!message.trace;
    return (
        <div className={`rounded-2xl p-3 ${isUser
            ? 'bg-brand-50 dark:bg-brand-900/20 ml-8'
            : 'bg-gray-50 dark:bg-gray-700/40 mr-8'}`}>
            <div className="flex items-center gap-2 mb-1">
                <span className={`badge ${isUser ? 'badge-success' : 'badge-info'}`}>{message.role}</span>
                {message.type && message.type !== 'TEXT' && <span className="badge badge-neutral">{message.type}</span>}
                {message.status && message.status !== 'DONE' && <span className="badge badge-warning">{message.status}</span>}
                {message.feedback?.rating && (
                    <span
                        className={`badge ${message.feedback.rating === 'UP' ? 'badge-success' : 'badge-warning'}`}
                        data-testid="admin-feedback-badge"
                        title={message.feedback.comment || undefined}
                    >
                        {message.feedback.rating === 'UP' ? '👍' : '👎'}
                    </span>
                )}
                <span className="text-xs text-gray-400 ml-auto">{fmt(message.createdAt)}</span>
            </div>
            {message.feedback?.comment && (
                <div className="text-xs text-amber-700 dark:text-amber-400 mt-1" data-testid="admin-feedback-comment">
                    Комментарий к оценке: {message.feedback.comment}
                </div>
            )}
            {message.text && (
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{message.text}</div>
            )}
            {message.errorMessage && <div className="text-sm text-red-500 mt-1">Ошибка: {message.errorMessage}</div>}
            {message.attachments && message.attachments.length > 0 && (
                <div className="text-xs text-gray-400 mt-1">Вложений: {message.attachments.length}</div>
            )}
            {message.payload != null && <PayloadView payload={message.payload} />}
            {hasTrace && (
                <div className="mt-2 border-t border-gray-200 dark:border-gray-600 pt-2">
                    <button className="text-xs font-medium text-primary-600 hover:underline" onClick={() => setShowTrace((s) => !s)}>
                        {showTrace ? '▼ Скрыть трейс агента' : '▶ Трейс агента (модель, промпты, тулы)'}
                    </button>
                    {showTrace && message.trace && <TraceView trace={message.trace} />}
                </div>
            )}
        </div>
    );
};

/** Человеко-читаемый payload: вещи и предложенные действия. */
const PayloadView: React.FC<{payload: unknown}> = ({payload}) => {
    const [raw, setRaw] = useState(false);
    const p = payload as {
        outfitItems?: {itemId?: string; name?: string; imageKey?: string}[];
        actions?: {title?: string; type?: string}[];
    };
    const items = p?.outfitItems ?? [];
    const actions = p?.actions ?? [];
    const openItem = (itemId?: string) => {
        if (itemId) window.open(`/clothing/${itemId}`, '_blank', 'noopener');
    };
    return (
        <div className="mt-2">
            {items.length > 0 && (
                <div>
                    <div className="text-xs text-gray-400 mb-1">Карточки вещей (клик — открыть вещь):</div>
                    <div className="flex flex-wrap gap-2">
                        {items.map((it, i) => (
                            <button key={i} type="button" title={it.itemId}
                                    onClick={() => openItem(it.itemId)}
                                    className="w-24 text-left hover:opacity-80 focus:outline-none">
                                <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                    {it.imageKey
                                        ? <AuthImage imageKey={it.imageKey} alt={it.name || ''} variant="thumbnail"
                                                     className="w-full h-full object-cover"/>
                                        : <span className="text-gray-400 text-xs">нет фото</span>}
                                </div>
                                <div className="text-[11px] text-gray-600 dark:text-gray-300 truncate mt-0.5">
                                    {it.name || 'вещь'}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {actions.length > 0 && (
                <div className="flex flex-wrap gap-1 items-center text-xs mt-1">
                    <span className="text-gray-400">Действия:</span>
                    {actions.map((a, i) => <span key={i} className="badge badge-info">{a.title || a.type}</span>)}
                </div>
            )}
            <button className="text-[11px] text-gray-400 hover:underline mt-1" onClick={() => setRaw((s) => !s)}>
                {raw ? 'скрыть raw payload' : 'raw payload'}
            </button>
            {raw && <Code text={JSON.stringify(payload, null, 2)} />}
        </div>
    );
};

/** Структурированный трейс: шапка → системный промпт → таймлайн итераций → лента сообщений → raw. */
const TraceView: React.FC<{trace: AgentTrace}> = ({trace}) => {
    const [showSystem, setShowSystem] = useState(false);
    const [showMessages, setShowMessages] = useState(false);
    const [showRaw, setShowRaw] = useState(false);
    const steps = trace.steps ?? [];
    return (
        <div className="mt-2 space-y-2 text-sm">
            <div className="flex flex-wrap gap-2 items-center text-xs">
                <span className="badge badge-neutral">модель: {trace.model || '—'}</span>
                <span className="badge badge-neutral">ключ: {trace.keySource || '—'}</span>
                <span className="badge badge-neutral">итераций: {steps.length}</span>
                {trace.toolNames && <span className="text-gray-400 truncate" title={trace.toolNames}>тулы: {trace.toolNames}</span>}
            </div>

            <Collapsible label="Системный промпт" open={showSystem} onToggle={() => setShowSystem((s) => !s)}>
                <div className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed
                                p-2 bg-gray-100 dark:bg-gray-900 rounded-lg max-h-72 overflow-y-auto">
                    {trace.systemPrompt}
                </div>
            </Collapsible>

            <div className="space-y-2">
                {steps.map((step, i) => <StepView key={i} step={step} />)}
            </div>

            {trace.messages && trace.messages.length > 0 && (
                <Collapsible label={`Полная лента модели (${trace.messages.length})`} open={showMessages}
                             onToggle={() => setShowMessages((s) => !s)}>
                    <div className="space-y-1">
                        {trace.messages.map((m, i) => (
                            <div key={i} className="text-xs p-2 bg-gray-100 dark:bg-gray-900 rounded">
                                <span className="font-mono text-gray-400 mr-2">{m.role}</span>
                                {m.images ? <span className="badge badge-neutral mr-1">📷 {m.images}</span> : null}
                                {m.toolName && <span className="badge badge-neutral mr-1">{m.toolName}</span>}
                                <span className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{m.content}</span>
                                {m.toolCalls?.map((tc, j) => (
                                    <div key={j} className="text-primary-600 font-mono">→ {tc.name}({tc.arguments})</div>
                                ))}
                            </div>
                        ))}
                    </div>
                </Collapsible>
            )}

            <button className="text-[11px] text-gray-400 hover:underline" onClick={() => setShowRaw((s) => !s)}>
                {showRaw ? 'скрыть raw трейс' : 'raw трейс (JSON)'}
            </button>
            {showRaw && <Code text={JSON.stringify(trace, null, 2)} />}
        </div>
    );
};

/** Одна итерация агента: ответ модели → вызовы тулов → результаты. */
const StepView: React.FC<{step: TraceStep}> = ({step}) => {
    const content = step.response?.content;
    const calls = step.response?.toolCalls ?? [];
    const execs = step.toolExecutions ?? [];
    return (
        <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-2">
            <div className="flex items-center gap-2 text-xs mb-1">
                <span className="font-semibold text-gray-700 dark:text-gray-200">
                    Итерация {step.iteration}{step.forcedFinal ? ' · финал' : ''}
                </span>
                {step.response?.finishReason && <span className="badge badge-neutral">{step.response.finishReason}</span>}
            </div>
            {content && (
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap mb-1">💬 {content}</div>
            )}
            {calls.map((tc, i) => (
                <div key={i} className="mt-1">
                    <div className="text-xs font-mono text-primary-600">🔧 {tc.name}</div>
                    {tc.arguments && tc.arguments !== '{}' && <Code text={pretty(tc.arguments)} small />}
                </div>
            ))}
            {execs.map((ex, i) => <ToolExec key={i} exec={ex} />)}
        </div>
    );
};

const ToolExec: React.FC<{exec: ToolExecution}> = ({exec}) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="mt-1 pl-2 border-l-2 border-brand-300 dark:border-brand-700">
            <button className="text-xs font-mono text-gray-500 dark:text-gray-400 hover:underline" onClick={() => setOpen((s) => !s)}>
                {open ? '▼' : '▶'} ↳ {exec.tool} → результат
            </button>
            {open && (
                <>
                    {exec.arguments && exec.arguments !== '{}' && (
                        <div className="text-[11px] text-gray-400 mt-1">аргументы:</div>
                    )}
                    {exec.arguments && exec.arguments !== '{}' && <Code text={pretty(exec.arguments)} small />}
                    <div className="text-[11px] text-gray-400 mt-1">результат:</div>
                    <Code text={pretty(exec.result)} small />
                </>
            )}
        </div>
    );
};

const Collapsible: React.FC<{label: string; open: boolean; onToggle: () => void; children: React.ReactNode}> =
    ({label, open, onToggle, children}) => (
        <div>
            <button className="text-xs font-medium text-gray-600 dark:text-gray-300 hover:underline" onClick={onToggle}>
                {open ? '▼' : '▶'} {label}
            </button>
            {open && <div className="mt-1">{children}</div>}
        </div>
    );

const Code: React.FC<{text: string; small?: boolean}> = ({text, small}) => (
    <pre className={`mt-1 p-2 bg-gray-100 dark:bg-gray-900 rounded-lg font-mono text-gray-700 dark:text-gray-300
        whitespace-pre-wrap overflow-x-auto max-h-80 overflow-y-auto ${small ? 'text-[11px]' : 'text-xs'}`}>
        {text}
    </pre>
);

export default StylistAdmin;
